import {useQueryClient} from "@tanstack/react-query";
import {type Coordinates, coordinatesByLocationId} from "@/api/apiClient";

type FetchCoordinatesParams = {
    locationId: string;
    suggestSessionId: string;
};

/**
 * Hook that provides a trigger function to fetch coordinates on demand
 *
 * This is a lazy query pattern - the query doesn't run automatically,
 * but only when you call the returned trigger function (e.g., on user click).
 *
 */
export function useFetchCoordinatesByLocationIdQuery() {
    const queryClient = useQueryClient();

    const fetchCoordinates = async ({
        locationId,
        suggestSessionId,
    }: FetchCoordinatesParams): Promise<Coordinates> => {
        return queryClient.fetchQuery({
            queryKey: [`/self-service/location-suggestions/${encodeURIComponent(locationId)}`],
            queryFn: async () => {
                try {
                    return await coordinatesByLocationId(locationId, suggestSessionId);
                } catch (error) {
                    console.error("Error fetching coordinates:", {
                        locationId,
                        suggestSessionId,
                        error,
                    });
                    throw error;
                }
            },
            retry: 3,
            retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        });
    };

    return {fetchCoordinates};
}
